import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Briefcase, GraduationCap, Building2, User } from "lucide-react";
import { useAuth } from "../contexts";

const ACCOUNT_TYPES = [
  { key: "creative", label: "Creative", sub: "Directors · Designers · Producers · Photo / Video", icon: Sparkles },
  { key: "talent", label: "Talent", sub: "Artists · A&R · Managers · DJs · Promoters", icon: Briefcase },
  { key: "education", label: "Education", sub: "Engineers · Consultants · Marketing", icon: GraduationCap },
  { key: "employer", label: "Employer", sub: "Labels · Brands · Studios · Event Co.", icon: Building2 },
  { key: "fan", label: "Fan", sub: "Just here for the culture", icon: User },
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    email: "", password: "", username: "", display_name: "",
    account_type: "fan", service_title: "", company_name: "",
    city: "", state: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register({ ...form, is_artist: ["creative", "talent"].includes(form.account_type) });
      const next = localStorage.getItem("hc_next");
      localStorage.removeItem("hc_next");
      navigate(next || (form.account_type === "employer" ? "/culture-connect" : "/studio"));
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not register.");
      setStep(0);
    } finally { setBusy(false); }
  };

  const canStep1 = form.account_type;
  const canSubmit = form.display_name && form.username && form.email && form.password.length >= 6;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 pb-32" data-testid="register-page">
      <div className="font-mono-label text-[10px] text-[var(--accent)] mb-3">JOIN CULTURE CONNECT</div>
      <h1 className="font-display text-5xl sm:text-6xl uppercase leading-none mb-3">Create Account</h1>
      <p className="font-mono-label text-[11px] text-neutral-500 mb-10">
        STEP {step + 1} OF 2 · {step === 0 ? "PICK YOUR ROLE" : "YOUR DETAILS"}
      </p>

      {step === 0 && (
        <div className="space-y-3" data-testid="signup-step-0">
          {ACCOUNT_TYPES.map((t) => {
            const Icon = t.icon;
            const active = form.account_type === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => update("account_type", t.key)}
                className={`w-full text-left border ${active ? "border-[var(--accent)] bg-[#1a0a05]" : "border-neutral-800"} p-5 hover:border-neutral-600 transition flex items-center gap-4`}
                data-testid={`signup-type-${t.key}`}
              >
                <div className={`w-12 h-12 border ${active ? "border-[var(--accent)] text-[var(--accent)]" : "border-neutral-700 text-neutral-400"} flex items-center justify-center shrink-0`}>
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-headline text-xl uppercase ${active ? "text-[var(--accent)]" : ""}`}>{t.label}</div>
                  <div className="font-mono-label text-[10px] text-neutral-500 mt-1">{t.sub}</div>
                </div>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => canStep1 && setStep(1)}
            disabled={!canStep1}
            className="btn-primary w-full justify-center mt-6"
            data-testid="signup-continue"
          >
            Continue
          </button>
        </div>
      )}

      {step === 1 && (
        <form onSubmit={submit} className="space-y-4" data-testid="signup-step-1">
          <input required placeholder="Display Name" value={form.display_name} onChange={(e)=>update("display_name",e.target.value)} className="input-brut" data-testid="register-display" />
          <input required placeholder="Username (no spaces, lowercase)" value={form.username} onChange={(e)=>update("username",e.target.value.replace(/\s+/g, "").toLowerCase())} className="input-brut" data-testid="register-username" />
          <input type="email" required placeholder="Email" value={form.email} onChange={(e)=>update("email",e.target.value)} className="input-brut" data-testid="register-email" />
          <input type="password" required placeholder="Password (min 6 chars)" value={form.password} onChange={(e)=>update("password",e.target.value)} className="input-brut" data-testid="register-password" />

          {form.account_type === "employer" ? (
            <input placeholder="Company name" value={form.company_name} onChange={(e)=>update("company_name",e.target.value)} className="input-brut" data-testid="register-company" />
          ) : form.account_type !== "fan" ? (
            <input placeholder="Service title (e.g. Videographer, Mixing Engineer)" value={form.service_title} onChange={(e)=>update("service_title",e.target.value)} className="input-brut" data-testid="register-service-title" />
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <input placeholder="City (optional)" value={form.city} onChange={(e)=>update("city",e.target.value)} className="input-brut" data-testid="register-city" />
            <select value={form.state} onChange={(e)=>update("state",e.target.value)} className="input-brut" data-testid="register-state">
              <option value="">State (optional)</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {err && <p className="text-[var(--accent)] font-mono-label text-xs" data-testid="register-error">{err}</p>}

          <div className="flex gap-2">
            <button type="button" onClick={() => setStep(0)} className="btn-ghost">Back</button>
            <button type="submit" disabled={busy || !canSubmit} className="btn-primary flex-1 justify-center" data-testid="register-submit">
              {busy ? "Creating…" : "Create Account"}
            </button>
          </div>
        </form>
      )}

      <p className="mt-8 font-mono-label text-[11px] text-neutral-400">
        ALREADY IN? <Link to="/login" className="text-[var(--accent)] hover:underline">SIGN IN</Link>
        <span className="mx-2 text-neutral-700">·</span>
        <Link to="/forgot-password" className="text-neutral-500 hover:text-white">FORGOT PASSWORD?</Link>
      </p>
    </div>
  );
}
